import React, { useState } from 'react';
import {
  BookText, Loader2, Library, Mail, Cable, Coins, Landmark, Timer, ScanSearch, ListChecks, Hash, Download,
} from 'lucide-react';
import {
  XbPage, WithScheme, SchemeHeading, ProvisionalNotice, Card, Fact, C, Table, Notice, useSchemeDetail, errorText,
} from './common';
import xbApi from '../../services/xbApi';
import { label, ruleCheck } from '../../utils/crossBorder';

const SEVERITY = {
  FAIL: 'bg-red-50 text-red-700 border-red-200',
  WARN: 'bg-amber-50 text-amber-800 border-amber-200',
  INFO: 'bg-gray-50 text-gray-600 border-gray-200',
};

/**
 * The chosen scheme's profile, readable: where it comes from, the messages
 * and wire format, currencies and corridors, the FX, liquidity and settlement
 * terms, the reply terms, screening, every rule the scheme checks and the
 * reason code it answers with.
 */
export default function XbProfile() {
  return (
    <XbPage icon={BookText} title="Scheme profile" wide
      intro="What the scheme expects of you, as the Sandbox applies it. Everything the switch checks under this scheme is here.">
      <WithScheme>{(scheme) => <ProfileBody scheme={scheme} />}</WithScheme>
    </XbPage>
  );
}

function ProfileBody({ scheme }) {
  const { detail, error } = useSchemeDetail(scheme);
  const [severity, setSeverity] = useState('ALL');
  const [downloadError, setDownloadError] = useState(null);

  if (error) return <Notice tone="danger" title="The profile could not be read">{error}</Notice>;
  if (!detail) {
    return <p className="flex items-center gap-2 text-sm text-gray-500 py-6">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Reading the profile…</p>;
  }

  const s = detail.summary;
  const p = detail.profile;
  const reasons = p.reasons || {};
  const rules = (p.rules || []).filter((r) => severity === 'ALL' || r.severity === severity);

  const downloadWatchlist = async () => {
    setDownloadError(null);
    try {
      const { blob, filename } = await xbApi.watchlist(s.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(errorText(e));
    }
  };

  return (
    <>
      <SchemeHeading summary={s} />
      <ProvisionalNotice summary={s} />

      <Card title="Where this profile comes from" icon={Library}>
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700">
          {s.sources.map((src) => <li key={src}>{src}</li>)}
        </ul>
        <p className="text-xs text-gray-500 mt-3">
          Version <C>{s.version}</C>{s.schemeRelease ? <> of scheme release <C>{s.schemeRelease}</C></> : null}.
          A published version never changes: content hash <span className="font-mono break-all">{s.contentHash}</span>.
        </p>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Messages" icon={Mail}>
          <Table head={['Message', 'Version used']} mono={[1]}
            rows={Object.entries(s.messages).map(([family, version]) => [family, version])} />
        </Card>

        <Card title="Wire format" icon={Cable}>
          <dl>
            <Fact label="Envelope">{label('envelope', s.wire.envelope)}</Fact>
            <Fact label="Signature">{label('signature', s.wire.signature)}</Fact>
            <Fact label="Transport">{label('transport', s.wire.transport)}
              {s.wire.maxPollSeconds ? `, at least every ${s.wire.maxPollSeconds} s` : ''}</Fact>
            {s.wire.clearingSystem && <Fact label="Clearing system" mono>{s.wire.clearingSystem}</Fact>}
            {s.wire.hubMemberId && <Fact label="The scheme's member id" mono>{s.wire.hubMemberId}</Fact>}
            {s.wire.bizSvc && <Fact label="Business service (BizSvc)" mono>{s.wire.bizSvc}</Fact>}
            {s.wire.memberIdPattern && <Fact label="Member ids" mono>{s.wire.memberIdPattern}</Fact>}
          </dl>
        </Card>

        <Card title="Currencies and FX" icon={Coins}>
          <dl>
            <Fact label="Currencies">{Object.entries(s.currencies).map(([c, d]) => `${c} (${d} decimals)`).join(', ')}</Fact>
            <Fact label="Conversion">{label('fx', s.fx?.model)}</Fact>
            {s.fx?.settlementCurrency && <Fact label="Settled in" mono>{s.fx.settlementCurrency}</Fact>}
            {s.fx?.bases?.length > 0 && <Fact label="Amount fixed by">{s.fx.bases.map((b) =>
              (b === 'FIXED_SEND' ? 'the sender (fixed send)' : 'the receiver (fixed receive)')).join(' or ')}</Fact>}
            {s.fx?.quoteChannel && <Fact label="Quotes">
              {s.fx.quotesLock ? `Locked when accepted${s.fx.quoteValidSeconds ? `, valid ${s.fx.quoteValidSeconds} s` : ''}`
                : 'Indicative: the scheme prices the payment itself'}</Fact>}
            {s.fx?.feePercent != null && <Fact label="Scheme fee">{s.fx.feePercent}% of the settlement amount</Fact>}
            {s.fx?.rateCard && <Fact label="Rates" mono>{s.fx.rateCard}</Fact>}
          </dl>
        </Card>

        <Card title="Liquidity and settlement" icon={Landmark}>
          <dl>
            <Fact label="Liquidity">{label('liquidity', s.liquidity)}</Fact>
            <Fact label="Settlement">{label('settlement', s.settlement?.model)}</Fact>
            {s.settlement?.windows?.length > 0 && <Fact label="Windows">{s.settlement.windows.join(', ')} ({s.settlement.timezone})</Fact>}
            {s.settlement?.afterCutOff && <Fact label="After the last window">
              {s.settlement.afterCutOff === 'NEXT_WINDOW' ? 'the next settlement day' : 'refused'}</Fact>}
            <Fact label="Statements">{s.offers.statements
              ? `camt.053${s.offers.statementRequest ? ', and on request with camt.060' : ''}` : 'none'}</Fact>
          </dl>
        </Card>

        <Card title="Answering a payment sent to you" icon={Timer}>
          <dl>
            <Fact label="Accept with"><C>{s.replies.incomingReply}</C> {label('level', s.replies.level)}</Fact>
            <Fact label="Answer within">{s.replies.responseSeconds} s</Fact>
            <Fact label="Hold for review">{s.replies.holdMaxSeconds > 0
              ? `PDNG allowed, decided within ${s.replies.holdMaxSeconds} s` : 'not allowed'}</Fact>
            {s.replies.confirmation && <Fact label="The scheme then confirms"><C>{s.replies.confirmation}</C></Fact>}
          </dl>
        </Card>

        <Card title="Screening" icon={ScanSearch}
          actions={s.offers.watchlist && (
            <button type="button" onClick={downloadWatchlist}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4" aria-hidden="true" /> Watch-list (CSV)
            </button>
          )}>
          {s.screening ? (
            <dl>
              <Fact label="Who screens">{s.screening.sides.map((x) => (x === 'SENDING' ? 'the sending side' : 'the receiving side')).join(' and ')}</Fact>
              <Fact label="Potential matches">{s.screening.hold ? 'held for review, then decided' : 'decided at once'}</Fact>
              {s.screening.watchlist && <Fact label="Watch-list" mono>{s.screening.watchlist}</Fact>}
            </dl>
          ) : <p className="text-sm text-gray-600">Not modelled under this scheme: nothing is screened.</p>}
          {s.offers.watchlist && (
            <p className="text-xs text-gray-500 mt-3">Synthetic names only, for loading into your own screening
              tool's test environment. Never use it as a real list.</p>
          )}
          {downloadError && <p className="text-xs text-red-600 mt-2">{downloadError}</p>}
        </Card>
      </div>

      <Card title={`Rules (${(p.rules || []).length})`} icon={ListChecks}
        actions={(
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium" role="group" aria-label="Severity">
            {['ALL', 'FAIL', 'WARN', 'INFO'].map((v) => (
              <button key={v} type="button" onClick={() => setSeverity(v)} aria-pressed={severity === v}
                className={`px-3 py-1 ${severity === v ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {v === 'ALL' ? 'All' : v}
              </button>
            ))}
          </div>
        )}>
        <p className="text-sm text-gray-600 mb-3">
          Each rule reads one element of the message. A <strong>FAIL</strong> refuses the payment with the code shown;
          a WARN or INFO is reported in <C>X-Profile-Findings</C> and lets it through.
        </p>
        <Table head={['Rule', 'Element', 'Check', 'If not met', 'Why']} mono={[0, 1]}
          rows={rules.map((r) => [
            r.id,
            `${r.message} ${r.path}`,
            ruleCheck(r),
            <span key="s" className="whitespace-nowrap">
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded border ${SEVERITY[r.severity] || ''}`}>{r.severity}</span>
              {r.severity === 'FAIL' && <> <C>{r.reasonCode || reasons[r.outcome] || 'NARR'}</C></>}
            </span>,
            r.text,
          ])} />
      </Card>

      <Card title="Reason codes" icon={Hash}>
        <p className="text-sm text-gray-600 mb-3">The code the scheme answers with for each kind of refusal.</p>
        <Table head={['When', 'Code']} mono={[1]}
          rows={Object.entries(reasons).map(([outcome, code]) => [outcome.toLowerCase().replaceAll('_', ' '), code])} />
      </Card>
    </>
  );
}
